import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { FormBuilder, Validators } from '@angular/forms';
import { BaseComponent } from '@core/base.component';
import { Address } from '@models/address';
import { FinancialInstitutionLender, MortgageeType } from '@models/lender';
import { TrustAccount } from '@models/trustAccount';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { BusinessState } from '@state/business';
import { TrustAccountDispatch } from '@state/business/trustAccounts/trustAccounts.dispatch';
import { TrustAccountsState } from '@state/business/trustAccounts/trustAccounts.state';
import { UploadDocument } from '@state/file-upload/fileUpload.actions';
import { FileUploadDispatch } from '@state/file-upload/fileUpload.dispatch';
import { FileUploadType } from '@state/file-upload/fileUpload.model';
import { cloneDeep } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { GoogleEncryptionService } from 'src/app/shared/api-repos/google-encryption/google-encryption.api.service';

interface VM {
	trustAccount: TrustAccount;
	voidChequeFile: File | null;
	voidChequeChanged: boolean;
	isEditingAccount: boolean;
	isDisabled: boolean;
	isLoading: boolean;
	saving: boolean;
	financialInstitution: Partial<FinancialInstitutionLender>;
}

const defaultVM = {
	trustAccount: {
		institutionId: null,
		id: null,
		address: null,
		nickname: '',
		payeeName: '',
		institutionName: '',
		institutionShortName: '',
		accountNumber: '',
		last3DigitsAccountNumber: null,
		transit: '',
		institutionNumber: '',
		voidCheque: null,
	},
	voidChequeFile: null,
	voidChequeChanged: false,
	isEditingAccount: false,
	isDisabled: true,
	isLoading: false,
	saving: false,
	financialInstitution: null,
};

@Component({
	selector: 'app-add-edit-trust-account',
	templateUrl: './add-edit-trust-account.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AddEditTrustAccountComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy
{
	mortgageeType = MortgageeType;
	changeSubscription: Subscription;
	_trustAccountId: string;
	trustAccountForm = this.formBuilder.group({
		nickname: ['', Validators.required],
		payeeName: ['', Validators.required],
		accountNumber: ['', Validators.required],
	});

	@Input() set trustAccountId(trustAccountId: string) {
		this._trustAccountId = trustAccountId;
		if (trustAccountId) {
			this.VM = {
				isLoading: true,
			};
			this.loadTrustAccount(trustAccountId);
		}
	}
	@Output() onClose = new EventEmitter<void>();
	@Output() onTrustAccountCreated = new EventEmitter<TrustAccount>();

	@Select(TrustAccountsState.get) trustAccount$: Observable<
		(id: string) => TrustAccount
	>;
	@ViewSelectSnapshot(BusinessState.businessId) businessId: string;

	constructor(
		private snackbarService: SnackbarService,
		private googleEncryptionService: GoogleEncryptionService,
		private trustAccountDispatch: TrustAccountDispatch,
		private fileUploadDispatch: FileUploadDispatch,
		private formBuilder: FormBuilder,
		private storage: AngularFireStorage,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.VM = defaultVM;
		this.changeSubscription = this.trustAccountForm.valueChanges
			.pipe(debounceTime(300))
			.subscribe((values) => {
				this.VM = {
					trustAccount: {
						...this.VM.trustAccount,
						...values,
					},
				};
				this.checkFormValidity();
			});
	}

	checkFormValidity(): void {
		this.VM = {
			isDisabled:
				this.trustAccountForm.invalid ||
				!this.VM.trustAccount.institutionName ||
				!this.VM.trustAccount.transit ||
				!this.VM.trustAccount.voidCheque,
		};
	}

	ngOnDestroy(): void {
		this.changeSubscription.unsubscribe();
	}

	async loadTrustAccount(id: string): Promise<void> {
		try {
			this.trustAccount$
				.pipe(take(1))
				.subscribe(async (getTrustAccountById) => {
					const _trustAccount = getTrustAccountById(id);
					if (_trustAccount) {
						const accountNumber =
							_trustAccount?.accountNumber != null
								? await this.googleEncryptionService
										.decrypt(_trustAccount?.accountNumber)
										.toPromise()
								: null;
						let voidCheque = null;
						let voidChequeFile = null;
						if (_trustAccount.voidCheque) {
							voidCheque = _trustAccount.voidCheque
								? await this.getVoidChequeImageUrl(
										this.relativeVoidChequePath(
											_trustAccount.id,
											_trustAccount.voidCheque,
										),
									)
								: null;
							voidChequeFile = {
								name: _trustAccount.voidCheque,
								type: 'image',
							} as File;
						}
						this.VM = {
							trustAccount: {
								...cloneDeep(_trustAccount),
								accountNumber,
								voidCheque,
							},
							voidChequeFile,
							voidChequeChanged: false,
							isEditingAccount: true,
							isLoading: false,
							financialInstitution: {
								id: _trustAccount.institutionId,
								name: _trustAccount.institutionName,
								shortName: _trustAccount.institutionShortName,
								institutionNumber: _trustAccount.institutionNumber,
								address: _trustAccount.address,
							},
						};
						this.trustAccountForm.patchValue(this.VM.trustAccount);
					} else {
						this.handleError(
							'Trust account not found',
							'Unable to load trust account',
						);
						this.VM = {
							isLoading: false,
						};
					}
				});
		} catch (error) {
			this.handleError(error, 'Unable to load trust account');
			this.VM = {
				isLoading: false,
			};
		}
	}

	onChangeFinancialInstitution(event): void {
		if (event) {
			this.VM = {
				trustAccount: {
					...this.VM.trustAccount,
					institutionName: event.financialInstitution.name,
					institutionNumber: event.financialInstitution.institutionNumber,
					institutionShortName: event.financialInstitution.shortName,
					institutionId: event.financialInstitution.id,
				},
				financialInstitution: event.financialInstitution,
			};
		} else {
			this.VM = {
				trustAccount: {
					...this.VM.trustAccount,
					institutionName: null,
					institutionNumber: null,
					institutionShortName: null,
					institutionId: null,
					transit: null,
					address: null,
				},
				financialInstitution: null,
			};
		}
		this.checkFormValidity();
	}

	onChangeBranch(
		event: { branch: { transitNumber: string; address: Address } } | null,
	): void {
		if (event) {
			this.VM = {
				trustAccount: {
					...this.VM.trustAccount,
					transit: event.branch.transitNumber,
					address: event.branch.address,
				},
			};
		} else {
			this.VM = {
				trustAccount: {
					...this.VM.trustAccount,
					transit: null,
					address: null,
				},
			};
		}
		this.checkFormValidity();
	}

	onVoidChequeChange(file: File): void {
		if (file) {
			this.VM = {
				voidChequeFile: file,
				voidChequeChanged: true,
			};
			this.VM = {
				trustAccount: {
					...this.VM.trustAccount,
					voidCheque: URL.createObjectURL(file),
				},
			};
		} else {
			this.VM = {
				voidChequeFile: null,
				voidChequeChanged: true,
			};
			this.VM = {
				trustAccount: {
					...this.VM.trustAccount,
					voidCheque: null,
				},
			};
		}
		this.checkFormValidity();
	}

	onFormSubmit(): void {
		if (!this.trustAccountForm.valid) return;
		this.saveTrustAccount();
	}

	async saveTrustAccount(): Promise<void> {
		this.VM = {
			isDisabled: true,
		};
		this.VM = {
			saving: true,
		};
		const encryptedAccountNumber = await this.googleEncryptionService
			.encrypt(this.VM.trustAccount.accountNumber)
			.toPromise();
		const toBeSaved = {
			...this.VM.trustAccount,
			...this.trustAccountForm.value,
			last3DigitsAccountNumber: this.VM.trustAccount.accountNumber.slice(-3),
			accountNumber: encryptedAccountNumber,
			voidCheque: this.relativeVoidChequePath(
				this.VM.trustAccount.id,
				this.VM.voidChequeFile.name,
			),
		};
		try {
			let savedAccount;
			if (this.VM.trustAccount.id) {
				savedAccount = await this.trustAccountDispatch.UpdateTrustAccount(
					this.VM.trustAccount.id,
					toBeSaved,
				);
				// Only upload if user actually changed the void cheque AND it's a valid File with content
				if (
					this.VM.voidChequeFile &&
					this.VM.voidChequeChanged &&
					this.VM.voidChequeFile instanceof File &&
					this.VM.voidChequeFile.size > 0
				) {
					await this.uploadVoidCheque(this.VM.trustAccount.id);
				}
			} else {
				savedAccount = await this.trustAccountDispatch.Create(toBeSaved);
				this.onTrustAccountCreated.emit(savedAccount.payload);
				// Only upload if user provided a void cheque AND it's a valid File with content
				if (
					this.VM.voidChequeFile &&
					this.VM.voidChequeFile instanceof File &&
					this.VM.voidChequeFile.size > 0
				) {
					await this.uploadVoidCheque(savedAccount.payload.id);
					toBeSaved.voidCheque = this.relativeVoidChequePath(
						savedAccount.payload.id,
						this.VM.voidChequeFile.name,
					);
					savedAccount = await this.trustAccountDispatch.UpdateTrustAccount(
						savedAccount.payload.id,
						toBeSaved,
					);
				}
			}
			this.close();
		} catch (error) {
			this.handleError(error, 'Error saving trust account');
		} finally {
			this.VM = {
				isDisabled: false,
				saving: false,
			};
		}
	}

	async onDeleteTrustAccount(): Promise<void> {
		try {
			if (!this.VM.trustAccount.id) {
				throw new Error('Trust account ID is missing');
			}
			await this.deleteVoidChequeImage(this.VM.trustAccount.id);
			await this.trustAccountDispatch.Remove(this.VM.trustAccount.id);
			this.close();
		} catch (error) {
			this.handleError(error, 'Error deleting trust account');
		}
	}

	private async deleteVoidChequeImage(trustAccountId: string): Promise<void> {
		try {
			const voidChequePath = this.relativeVoidChequePath(
				trustAccountId,
				this.VM.voidChequeFile.name,
			);
			const ref = this.storage.ref(voidChequePath);
			await ref.delete().toPromise();
		} catch (error) {
			console.error('Error deleting voidCheque image:', error);
		}
	}

	uploadVoidCheque(trustAccountId: string) {
		this.fileUploadDispatch.UploadFile({
			uploadType: FileUploadType.VOID_CHEQUE,
			name: this.VM.voidChequeFile.name,
			docId: this.VM.trustAccount.id,
			path: this.relativeVoidChequePath(
				trustAccountId,
				this.VM.voidChequeFile.name,
			),
			file: this.VM.voidChequeFile,
		}) as UploadDocument<any>;
	}

	relativeVoidChequePath(trustAccountId: string, voidChequeName): string {
		return `${this.businessId}/trustAccounts/${trustAccountId}/voidCheque.${this.getFileExtension(voidChequeName)}`;
	}

	async getVoidChequeImageUrl(voidChequePath: string): Promise<string> {
		try {
			const ref = this.storage.ref(voidChequePath);
			const downloadUrl = await ref.getDownloadURL().toPromise();
			return downloadUrl;
		} catch (e) {
			console.error('Error getting void cheque image URL:', e);
			return null;
		}
	}

	getFileExtension(fileName: string): string {
		const extension = fileName.split('.').pop();
		return extension.toLowerCase();
	}

	close(): void {
		this.onClose.emit();
	}

	handleError(error: any, message: string): void {
		console.error(error); // Or LogRocket.captureException(error);
		this.snackbarService.openSnackBar({ text: message, type: 'error' });
	}
}
